import { FC, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { calculateProfileCompleteness } from '@/utils/profileUtils';
import { Service } from '@/types/dashboard';

interface UserActivity {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  last_update: string | null;
  completeness?: number;
}

export const AdminSettings: FC = () => {
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Fetch the user's profile to check their role
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
            
          if (!profileError && profileData && profileData.role === 'admin') {
            setIsAdmin(true);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);
  
  const fetchUserActivity = async () => {
    try {
      setLoading(true);
      
      // Fetch users with their last activity time
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, last_update, company_name, profile_image_url, banner_image_url, whatsapp_number, currency, country, preferred_messaging, profession, profile_bio')
        .order('last_update', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      // For each user, fetch their services and calculate completeness
      const usersWithCompleteness = await Promise.all((data as UserActivity[]).map(async (user: any) => {
        const { data: services, error: servicesError } = await supabase
          .from('services')
          .select('*')
          .eq('user_id', user.id);
        const completeness = calculateProfileCompleteness(user, services || []).overallScore;
        return { ...user, completeness };
      }));
      
      setUserActivity(usersWithCompleteness);
    } catch (error) {
      console.error('Error fetching user activity:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.adminSettings')}</CardTitle>
          <CardDescription>{t('common.loading')}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center p-6">
          <Loader className="h-8 w-8 animate-spin text-gebeya-pink" />
        </CardContent>
      </Card>
    );
  }
  
  if (!isAdmin) {
    return null;
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.adminSettings')}</CardTitle>
        <CardDescription>
          {t('settings.adminDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-2">{t('settings.userActivity')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('settings.userActivityDescription')}
            </p>
            
            <Button 
              onClick={fetchUserActivity}
              variant="outline"
              className="mb-4"
            >
              {loading ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('settings.loadUserActivity')
              )}
            </Button>
            
            {userActivity.length > 0 && (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('settings.user')}</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>{t('settings.lastActive')}</TableHead>
                      <TableHead>Completeness</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userActivity.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.first_name || user.last_name 
                            ? `${user.first_name || ''} ${user.last_name || ''}`.trim() 
                            : t('settings.unknownUser')}
                        </TableCell>
                        <TableCell>{user.email || t('settings.noEmail')}</TableCell>
                        <TableCell>
                          {user.last_update 
                            ? formatDistanceToNow(new Date(user.last_update), { addSuffix: true })
                            : t('settings.never')}
                        </TableCell>
                        <TableCell>
                          {typeof user.completeness === 'number' ? `${user.completeness}%` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 